/**
 * Onboarding Dialog — a self-contained intro to Outclose for first-time visitors.
 *
 * A single screen: what Outclose is plus its core features (curated dinners,
 * Kikis, the event index), with a pinned "Meet your People" CTA that stays
 * visible while the content scrolls, and a "Sign in" link for returning users.
 * The dialog chrome (backdrop, animated card) mirrors <SignInDialog /> so it
 * feels native.
 */

import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, UtensilsCrossed, PartyPopper, Compass } from "lucide-react";
import logo from "../assets/icons/outclose.svg";
import { useScrollLock } from "../lib/useScrollLock";
import { withPostSignupNav } from "../lib/postSignupNav";
import { useAuth } from "@/lib/platform";

interface OnboardingFeature {
  icon: React.ReactNode;
  /** Leads with the value prop — a statement or question that hooks the reader. */
  heading: string;
  /** Explains the feature behind the heading. */
  body: string;
}


const TITLE = "More of us, more often.";
const INTRO =
  " That's the entire mission. Outclose exists to close the distance between you and your community.";

const FEATURES: OnboardingFeature[] = [
  {
    icon: <PartyPopper className="w-5 h-5" />,
    heading: "Find your crew.",
    body: "Start a Kiki for whatever you're into. A hike, the old movie none of your friends are in the mood for? Find the ones who actually show up.",
  },
  {
    icon: <UtensilsCrossed className="w-5 h-5" />,
    heading: "We'll sit you with the right strangers.",
    body: "Every week we organize curated dinner experiences with small groups of compatible strangers that leave as friends.",
  },
  {
    icon: <Compass className="w-5 h-5" />,
    heading: "Everything gay in your city, one place.",
    body: "We pull together the LGBTQ+ events near you, so you're never the last to know.",
  },
];

export interface OnboardingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * App path to return the user to after they sign up and finish the quiz
   * (e.g. the event they were viewing, "/events/some-event"). Forwarded to the quiz
   * as a `nav` param so it redirects there on completion. Used by the default
   * `signUpHref`; the in-app gate also threads this through `onSignUp`.
   */
  postSignupUrl?: string;
  /** Where the primary CTA links when no `onSignUp` is given (defaults to the app quiz). */
  signUpHref?: string;
  /** Hide the sign-up / sign-in footer — the viewer already has an account. */
  hideAuthActions?: boolean;
}

export function OnboardingDialog({
  isOpen,
  onClose,
  postSignupUrl,
  signUpHref = "https://app.outclose.com/quiz",
  hideAuthActions = false,
}: OnboardingDialogProps) {
  // Stop the page behind the modal from scrolling while it's open.
  useScrollLock(isOpen);
  const { openSignIn } = useAuth();

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />

          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative flex flex-col bg-neutral-50/95 dark:bg-neutral-900/95 rounded-2xl shadow-2xl max-w-md w-full max-h-[95vh] overflow-hidden my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Scrollable content */}
            <div className="relative flex-1 overflow-y-auto p-6 pt-8 text-center">
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-black/5 dark:hover:bg-neutral-800 transition-colors"
              >
                <X className="w-5 h-5 text-neutral-500 dark:text-neutral-300" />
              </button>

              <div className="flex items-center justify-center mb-4">
                <img src={logo} alt="Outclose" className="w-12 h-12 dark:invert" />
              </div>

              <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-2 font-Poppins">
                {TITLE}
              </h2>
              <p className="text-dark-muted dark:text-neutral-400 leading-relaxed">
                {INTRO}
              </p>

              <ul className="mt-6 space-y-4 text-left">
                {FEATURES.map((f) => (
                  <li key={f.heading} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-500/10 text-primary-500">
                      {f.icon}
                    </span>
                    <div>
                      <p className="font-semibold text-neutral-900 dark:text-neutral-100 leading-snug">
                        {f.heading}
                      </p>
                      <p className="mt-0.5 text-sm text-dark-muted dark:text-neutral-400 leading-snug">
                        {f.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pinned footer — stays visible while the content above scrolls.
                Hidden for signed-in viewers, who need neither CTA. */}
            {!hideAuthActions && (
            <div className="shrink-0 border-t border-black/5 dark:border-white/10 bg-neutral-50/95 dark:bg-neutral-900/95 backdrop-blur px-6 py-4 flex flex-col gap-3 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.18)]">
              <a href={withPostSignupNav(signUpHref, postSignupUrl)} className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                Meet your People <Sparkles className="w-4 h-4" />
              </a>
              <p className="text-center text-sm text-neutral-500 dark:text-neutral-400">
                Already have an account?{" "}
                  <button
                    onClick={() => { openSignIn(); onClose(); }}
                    className="font-semibold text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    Sign in
                  </button>
              </p>
            </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default OnboardingDialog;
